import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Container,
    Typography,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    MenuItem,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    Icon
} from "@mui/material";
import { Add, Delete, Edit, PlayArrow, Visibility } from "@mui/icons-material";
import API from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";

interface DataSet {
    dataSetID: string;
    dataSetName: string;
    dataSourceType?: string;
    remoteConnectionString?: string;
    webAPIEndpoint?: string;
    authToken?: string;
    createdAt: string;
    createdBy: string;
    isSystem: boolean;
    deleted: boolean;
    tenantID?: string;
    query?: string;
    responsePath?: string;
    filterClause?: string;
}

const DataSourceTypes = ["Table", "Remote DB", "Web API"];

const DataSetsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [datasets, setDatasets] = useState<DataSet[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState<DataSet | null>(null);
    const [form, setForm] = useState<Partial<DataSet>>({});
    const [availableTables, setAvailableTables] = useState<any[]>([]);

    const fetchDataSets = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };
            const res = await API.get("/datasets", headers);
            setDatasets(res.data);
        } catch (err) {
            console.error("Failed to fetch datasets", err);
        }
    };

    const fetchTables = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };
            const res = await API.get("/tables", headers);
            setAvailableTables(res.data);
        } catch (err) {
            console.error("Failed to fetch tables", err);
        }
    };

    useEffect(() => {
        fetchDataSets();
        fetchTables();
    }, []);

    const handleSave = async () => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId,
            },
        };

        try {
            if (editing) {
                await API.put(`/datasets/${editing.dataSetID}`, {
                    ...editing,
                    ...form,
                    updatedBy: user.user?.userID,
                    updatedAt: new Date().toISOString(),
                }, headers);
            } else {
                await API.post(`/datasets`, {
                    ...form,
                    createdBy: user.user?.userID,
                    tenantID: user.user?.tenantID,
                }, headers);
            }
            fetchDataSets();
            handleCloseDialog();
        } catch (err) {
            console.error("Save failed", err);
        }
    };

    const handleDelete = async (id: string) => {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = {
            headers: {
                authToken: user.user?.authtoken,
                sessionId: user.user?.sessionId,
            },
        };
        try {
            await API.delete(`/datasets/${id}`, headers);
            fetchDataSets();
        } catch (err) {
            console.error("Delete failed", err);
        }
    };

    const handleOpenDialog = (dataset?: DataSet) => {
        setEditing(dataset || null);
        setForm(dataset ? { ...dataset } : { dataSourceType: "Table" });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditing(null);
        setForm({});
    };

    const handleTestQuery = () => {
        alert("Query test feature is not yet implemented.");
    };

    const handleLaunchPathEditor = () => {
        navigate("/tools/response-path-editor");
    };

    return (
        <Box sx={{ bgcolor: theme.palette.background.default, color: theme.palette.text.primary, minHeight: '100vh', pt: 10 }}>
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">Datasets</Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                        New Dataset
                    </Button>
                </Box>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Endpoint</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {datasets.map((ds) => (
                            <TableRow key={ds.dataSetID}>
                                <TableCell>{ds.dataSetName}</TableCell>
                                <TableCell>{ds.dataSourceType}</TableCell>
                                <TableCell>{ds.webAPIEndpoint}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Edit">
                                        <IconButton onClick={() => handleOpenDialog(ds)}><Edit /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton onClick={() => handleDelete(ds.dataSetID)}><Delete /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{editing ? "Edit Dataset" : "New Dataset"}</DialogTitle>
                    <DialogContent dividers>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Dataset Name"
                                fullWidth
                                required
                                value={form.dataSetName || ""}
                                onChange={(e) => setForm({ ...form, dataSetName: e.target.value })}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Source Type</InputLabel>
                                <Select
                                    value={form.dataSourceType || ""}
                                    onChange={(e) => {
                                        const newType = e.target.value;
                                        setForm({
                                            dataSourceType: newType,
                                            remoteConnectionString: "",
                                            webAPIEndpoint: "",
                                            query: "",
                                            responsePath: "",
                                            filterClause: "",
                                        });
                                    }}
                                    label="Source Type"
                                >
                                    {DataSourceTypes.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {form.dataSourceType === "Table" && (
                                <>
                                    <FormControl fullWidth>
                                        <InputLabel>Select Table</InputLabel>
                                        <Select
                                            value={form.remoteConnectionString || ""}
                                            onChange={(e) => setForm({ ...form, remoteConnectionString: e.target.value })}
                                            label="Select Table"
                                        >
                                            {availableTables.map((table) => (
                                                <MenuItem key={table.tableID} value={table.tableID}>{table.tableName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label="Filter Clause (optional)"
                                        fullWidth
                                        placeholder="e.g. @IsActive=1"
                                        value={form.filterClause || ""}
                                        onChange={(e) => setForm({ ...form, filterClause: e.target.value })}
                                    />
                                </>
                            )}

                            {form.dataSourceType === "Remote DB" && (
                                <>
                                    <TextField
                                        label="Remote Connection String"
                                        fullWidth
                                        placeholder="e.g. Server=tcp:...;Database=...;User Id=..."
                                        value={form.remoteConnectionString || ""}
                                        onChange={(e) => setForm({ ...form, remoteConnectionString: e.target.value })}
                                    />
                                    <TextField
                                        label="SQL Query"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder="SELECT * FROM Users WHERE @IsActive = 1"
                                        value={form.query || ""}
                                        onChange={(e) => setForm({ ...form, query: e.target.value })}
                                    />
                                    <Button startIcon={<PlayArrow />} onClick={handleTestQuery}>Test Query</Button>
                                </>
                            )}

                            {form.dataSourceType === "Web API" && (
                                <>
                                    <TextField
                                        label="Web API Endpoint (GET)"
                                        fullWidth
                                        value={form.webAPIEndpoint || ""}
                                        onChange={(e) => setForm({ ...form, webAPIEndpoint: e.target.value })}
                                    />
                                    <TextField
                                        label="Auth Token (optional)"
                                        fullWidth
                                        value={form.authToken || ""}
                                        onChange={(e) => setForm({ ...form, authToken: e.target.value })}
                                    />
                                    <TextField
                                        label="Response Path"
                                        fullWidth
                                        value={form.responsePath || ""}
                                        placeholder="e.g. $.records[*] or /root/items"
                                        onChange={(e) => setForm({ ...form, responsePath: e.target.value })}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Open Response Path Editor">
                                                        <IconButton onClick={handleLaunchPathEditor}>
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave}>Save</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default DataSetsPage;
